import { memo, useCallback, useEffect, useState } from 'react';
import {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { GameThumbnail } from '@betty/games';
import {
  BottomSheet,
  useNavigation,
  StackNavigationProp,
  resetToRootNavigator,
} from '@betty/navigation';
import { Box, Text, ParallaxList, Image, Icon, Button } from '@betty/moonlight';
import { RewardReceivedBottomSheetProps } from './RewardReceivedBottomSheet.types';
import { NotificationReward, RewardType } from '../../state/rewards.types';
import { formatRewardAmount } from './RewardReceivedBottomSheet.utils';
import {
  DEFAULT_ANIMATION_DURATION,
  TEXT_ANIMATION_DURATION,
  IMAGE_COMMON_PROPS,
  REWARD_BUTTON_TEXTS,
  REWARD_ICONS,
  REWARD_IMAGES,
  REWARD_TEXTS,
  DEFAULT_ANIMATION_DELAY,
} from './RewardReceivedBottomSheet.content';
import QuestCompleted from './QuestCompleted/QuestCompleted';

function RewardReceivedBottomSheet({
  rewards,
  questCompleted,
  close,
}: RewardReceivedBottomSheetProps) {
  const [activeIndex, setActiveIndex] = useState(Number(rewards.length > 2));
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const animatedValue = useSharedValue(Number(!questCompleted));
  const textAnimatedValue = useSharedValue(1);
  const activeReward = rewards[activeIndex];
  const { rewardType, gameIds } = activeReward;

  const renderItem = useCallback(
    ({ rewardType, gameIds }: NotificationReward) => {
      const [gameId] = gameIds || [];

      if (gameId !== undefined) {
        return (
          <GameThumbnail
            gameId={gameId}
            type="square"
            {...IMAGE_COMMON_PROPS}
          />
        );
      }

      if (rewardType === RewardType.FreeSpins) {
        return null;
      }

      return <Image name={REWARD_IMAGES[rewardType]} {...IMAGE_COMMON_PROPS} />;
    },
    [],
  );

  const onButtonPress = useCallback(() => {
    close();

    if (rewardType === RewardType.Coins) {
      resetToRootNavigator(() => {
        navigation.navigate('Shop');
      });
    }

    if (rewardType === RewardType.FreeSpins && gameIds) {
      resetToRootNavigator(() => {
        navigation.navigate('GameplayScreen', { id: gameIds[0] });
      });
    }

    if (rewardType === RewardType.InstantWheel) {
      // TODO: check types
      BottomSheet.open({
        namespace: 'instantWheel',
        type: 'InstantWheel',
      });
    }
  }, [rewardType, gameIds, navigation, close]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
  }));

  const textAnimatedStyle = useAnimatedStyle(() => ({
    opacity: textAnimatedValue.value,
    transform: [
      {
        scale: textAnimatedValue.value,
      },
    ],
  }));

  useEffect(() => {
    textAnimatedValue.value = 0;
    textAnimatedValue.value = withSpring(1, {
      duration: TEXT_ANIMATION_DURATION,
    });
  }, [activeIndex, textAnimatedValue]);

  useEffect(() => {
    if (questCompleted) {
      animatedValue.value = withDelay(
        DEFAULT_ANIMATION_DELAY + 2 * DEFAULT_ANIMATION_DURATION,
        withTiming(1, {
          duration: DEFAULT_ANIMATION_DURATION,
        }),
      );
    }
  }, [questCompleted, animatedValue]);

  const rewardIcon = (
    <Icon
      name={REWARD_ICONS[rewardType]}
      color="iconPrimary"
      width="space-400"
      height="space-400"
    />
  );

  return (
    <>
      <Box animatedStyle={animatedStyle}>
        <Text
          variant="overline"
          locNS="rewards"
          locKey="yourReward"
          textAlign="center"
          paddingTop="space-400"
          paddingBottom="space-200"
          color="accentSurfacePrimary"
        />
        <Text
          variant="h3"
          textAlign="center"
          color="accentSurfacePrimary"
          animatedStyle={textAnimatedStyle}
        >
          {formatRewardAmount(activeReward)}
        </Text>
        <Box
          height="space-1000"
          borderWidth="border-width-025"
          borderLeftWidth="border-width-0"
          borderRightWidth="border-width-0"
          borderColor="borderPrimary"
          marginVertical="space-400"
          paddingVertical="space-300"
        >
          <Box
            gap="space-300"
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            animatedStyle={textAnimatedStyle}
          >
            {rewardIcon}
            <Text
              variant="callout"
              locNS="rewards"
              locKey={REWARD_TEXTS[rewardType]}
              color="accentSurfacePrimary"
              textAlign="center"
            />
            {rewardIcon}
          </Box>
        </Box>
        <ParallaxList
          data={rewards}
          activeIndex={activeIndex}
          renderItem={renderItem}
          onChangeActiveIndex={setActiveIndex}
          height={220}
        />
        <Button
          onPress={onButtonPress}
          locKey={REWARD_BUTTON_TEXTS[rewardType]}
          locNS="rewards"
          marginTop="space-400"
          size="large"
          light
        />
      </Box>
      {questCompleted && <QuestCompleted />}
    </>
  );
}

export default memo(RewardReceivedBottomSheet);
